/**
 * What a node offers, read from its manifest: the approver's view of it, and
 * (with `selection`) the checkboxes that trim what it may offer.
 */

import type { NodeManifest, SurfaceDiff, SurfaceSelection } from '@aioe/shared';

import type { ReactNode } from 'react';

import { ListRow, StatusMark, Tick } from './ui.tsx';

type ListKey = 'roles' | 'runtimes' | 'models' | 'memory' | 'repos' | 'services';

const ROLE_LABEL: Record<string, string> = { teams: 'Agent teams', inference: 'Shared inference', dlp: 'DLP classifier', sandbox: 'Agent sandboxes', computer: 'Computer use' };

export function manifestItems(m: NodeManifest): Array<{ key: ListKey; label: string; items: Array<{ id: string; text: string }> }> {
  return [
    { key: 'roles', label: 'Offered for', items: m.roles.map((r) => ({ id: r, text: ROLE_LABEL[r] ?? r })) },
    { key: 'runtimes', label: 'Runtimes', items: m.runtimes.map((r) => ({ id: r.id, text: `${r.id}${r.version ? ` ${r.version}` : ''}${r.authenticated === false ? ' (not signed in)' : ''}` })) },
    { key: 'models', label: 'Models', items: m.models.map((r) => ({ id: r.id, text: `${r.id}${r.acceleration ? ` · ${r.acceleration.toUpperCase()}` : ''}` })) },
    { key: 'memory', label: 'Memory', items: memoryByKind(m) },
    { key: 'repos', label: 'Repositories', items: m.repos.map((r) => ({ id: r.rootId, text: r.remote ? `${r.remote}${r.branch ? ` (${r.branch})` : ''}` : r.rootId })) },
    { key: 'services', label: 'Services', items: Object.entries(m.services).filter(([, v]) => v !== undefined).map(([k, v]) => ({ id: k, text: `${k} :${v}` })) },
  ].filter((g) => g.items.length > 0) as Array<{ key: ListKey; label: string; items: Array<{ id: string; text: string }> }>;
}

/** Memory is approved by kind: one row per kind, saying which repositories carry it. A new root's graph is not new surface. */
function memoryByKind(m: NodeManifest): Array<{ id: string; text: string }> {
  const kinds = new Map<string, NodeManifest['memory']>();
  for (const r of m.memory) kinds.set(r.kind, [...(kinds.get(r.kind) ?? []), r]);
  return [...kinds].map(([kind, rs]) => {
    const scopes = [...new Set(rs.flatMap((r) => r.scopes))].join(', ');
    const roots = rs.map((r) => r.root).filter((x): x is string => !!x);
    return { id: kind, text: `${kind} · ${scopes}${roots.length ? ` · ${roots.length === 1 ? roots[0] : `${roots.length} repositories`}` : ''}` };
  });
}

function resourcesLine(m: NodeManifest): string {
  const parts = [m.resources.cpu ? `${m.resources.cpu} CPUs` : '', m.resources.ramGb ? `${m.resources.ramGb} GB RAM` : '', ...m.resources.gpus.map((g) => `${g.name}${g.vramGb ? ` ${g.vramGb} GB` : ''}`)].filter(Boolean);
  return parts.join(' · ');
}

type Fact = { key: string; label: string; content: ReactNode; status?: ReactNode; pickable?: boolean };

/** The manifest's facts in reading order: each offered list (with a checkbox per item when `onChange`), then resources, sandbox, home and its own limit. */
function manifestFacts({ manifest, selection, onChange }: { manifest: NodeManifest; selection?: SurfaceSelection; onChange?: (s: SurfaceSelection) => void }): Fact[] {
  const checked = (key: ListKey, id: string) => !selection?.[key] || (selection[key] as string[]).includes(id);
  const toggle = (key: ListKey, id: string, all: string[]) => {
    if (!onChange) return;
    const current = (selection?.[key] as string[] | undefined) ?? all;
    onChange({ ...selection, [key]: current.includes(id) ? current.filter((x) => x !== id) : [...current, id] });
  };
  const facts: Fact[] = manifestItems(manifest).map((g) => ({
    key: g.key,
    label: g.label,
    pickable: Boolean(onChange),
    content: onChange ? (
      <span className="flex flex-wrap gap-x-4 gap-y-1">
        {g.items.map((it) => (
          <Tick key={it.id} checked={checked(g.key, it.id)} onChange={() => toggle(g.key, it.id, g.items.map((x) => x.id))}>
            <span className={checked(g.key, it.id) ? '' : 'text-muted line-through'}>{it.text}</span>
          </Tick>
        ))}
      </span>
    ) : (
      g.items.map((it) => it.text).join(' · ')
    ),
  }));
  const resources = resourcesLine(manifest);
  if (resources) facts.push({ key: 'resources', label: 'Resources', content: <span className="tabular-nums">{resources}</span> });
  if (manifest.sandboxCheck) {
    const c = manifest.sandboxCheck;
    facts.push({
      key: 'sandbox',
      label: 'Sandbox',
      status: c.canSandbox ? <StatusMark state="connected" label="Agents run in a sandbox" /> : <StatusMark state="disabled" label="Cannot sandbox agents" />,
      content: `${c.reason ? `${c.reason} · ` : ''}Linux ${c.kernel}${c.landlockAbi !== undefined ? ` · Landlock ${c.landlockAbi}` : ''}${c.openshell ? ` · OpenShell ${c.openshell}` : ''}`,
    });
  }
  if (manifest.home) facts.push({ key: 'home', label: 'Home', content: <span className="mono break-all">{manifest.home}</span> });
  if (manifest.policy.maxConcurrentAgents !== undefined) facts.push({ key: 'limit', label: 'Its own limit', content: <span className="tabular-nums">{manifest.policy.maxConcurrentAgents} agents at once</span> });
  return facts;
}

/**
 * The manifest. By default as rows for a list group (Layout.tsx ListRow), one per fact, so the caller
 * puts it straight into a ListGroup; with `selection`, each offered item is a checkbox the approver
 * can clear. `layout="list"` is a compact term list for a panel that is not a list group (the enrol page).
 */
export function ManifestView({ manifest, selection, onChange, layout = 'rows' }: { manifest: NodeManifest; selection?: SurfaceSelection; onChange?: (s: SurfaceSelection) => void; layout?: 'rows' | 'list' }) {
  const facts = manifestFacts({ manifest, selection, onChange });
  if (layout === 'list') {
    return (
      <dl className="grid gap-2 text-sm">
        {facts.map((f) => (
          <div key={f.key} className="grid gap-1 sm:grid-cols-[8rem_1fr]">
            <dt className="text-muted">{f.label}</dt>
            <dd className="min-w-0">
              {f.status}
              {f.status ? <div className="text-xs text-muted">{f.content}</div> : f.content}
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return (
    <>
      {facts.map((f) =>
        f.pickable ? (
          <ListRow key={f.key} title={f.label}>
            {f.content}
          </ListRow>
        ) : (
          <ListRow key={f.key} title={f.label} description={f.content} status={f.status} />
        ),
      )}
    </>
  );
}

/** What a node now offers beyond its approval, as one sentence per category. */
export function SurfaceGrowth({ diff }: { diff: SurfaceDiff }) {
  const lines = (['roles', 'runtimes', 'models', 'memory', 'repos', 'services'] as const).filter((k) => diff.added[k].length).map((k) => `${k[0]!.toUpperCase()}${k.slice(1)}: ${diff.added[k].join(', ')}`);
  if (diff.added.sandbox) lines.push('Agent sandboxes');
  if (diff.added.computer) lines.push('Computer use');
  if (diff.added.canSandbox) lines.push('Runs agents in a sandbox: its start-up check now says it can');
  return (
    <ul className="grid gap-1 text-sm">
      {lines.map((l) => (
        <li key={l}>{l}</li>
      ))}
    </ul>
  );
}

export const parseTags = (text: string) =>
  text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
